import { useCallback, useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import TurnstileWidget from "../shared/TurnstileWidget.jsx";
import { API_BASE_URL } from "../../utils/api.js";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

/**
 * Replaces the EmailOctopus hosted-form embed. Nothing third-party is
 * loaded to render this: the address goes to our own API, which talks to
 * EmailOctopus with a server-side key (api/newsletter-subscribe).
 *
 * What that buys, beyond the design being ours: the old embed pulled a
 * script from eomail5.com plus fonts from Google, and its markup shipped
 * without accessible names — the component this replaces had to patch
 * aria-labels onto the widget with a MutationObserver. These labels are
 * our own.
 */

/** Must match the `Interests` field's options in EmailOctopus exactly —
 * these strings are the stored value, the visible text comes from the
 * locales. See INTERESTS in api/newsletter-subscribe/index.js. */
const INTERESTS = ["News", "Events", "Posts", "Fundraising"];

/** Allowed values of the `source` field. The form never shows this; each
 * placement passes its own, so the list records where someone signed up. */
export const NEWSLETTER_SOURCES = ["Home", "Newsletter", "LARPHub", "LARPDetails"];

const DATENSCHUTZ_PATH = { de: "datenschutz", en: "privacy" };

/** Everything the visitor fills in is mandatory. The API enforces the same
 * shape, so neither side can be relaxed on its own. */
const schema = z.object({
  email: z.string().min(1).email(),
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  interests: z.array(z.enum(INTERESTS)).min(1),
  honeypot: z.string().max(0), // bot trap, same shape as the contact form
});

const fieldStyle = {
  fontFamily: "Inter, sans-serif",
  fontSize: "15px",
  color: "var(--color-ink)",
  background: "var(--color-cream)",
  border: "1px solid rgba(10,9,7,0.2)",
  borderRadius: "8px",
  padding: "12px 14px",
  width: "100%",
};

const labelStyle = { display: "flex", flexDirection: "column", gap: "6px", fontSize: "13px", color: "rgba(10,9,7,0.65)" };
const errorStyle = { fontSize: "12px", color: "#b3261e", margin: 0 };
const checkRowStyle = { display: "flex", alignItems: "center", gap: "10px", fontSize: "14px", color: "var(--color-ink)", cursor: "pointer" };
const checkboxStyle = { width: "17px", height: "17px", accentColor: "var(--color-gold)", flex: "none" };

/**
 * Multi-select as a disclosure rather than a native `<select multiple>`:
 * the native control renders as a permanently expanded list box, which is
 * four rows of height in a form that sits in a narrow column, and on
 * touch devices its multi-select gesture is close to undiscoverable.
 *
 * The popover holds real checkboxes, so the semantics stay native —
 * that is what keeps this accessible where the EmailOctopus widget it
 * replaces was not. The button carries the summary as its accessible
 * name, Escape closes and returns focus, and a click outside dismisses.
 */
const POPOVER_MAX_HEIGHT = 200;

function InterestSelect({ value, onChange, invalid, labelId }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState({ up: false, maxHeight: POPOVER_MAX_HEIGHT });
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);

  /**
   * Work out where the list fits before showing it. Three things limit the
   * space and none of them is visible from CSS:
   *
   * - **The card clips it.** The nearest ancestor with a non-visible
   *   overflow — on the home page a `.tl-card-light`, which sets
   *   `overflow: hidden` for its rounded corners — cuts the list off at
   *   its own edge. That card is only as tall as its content, and the
   *   content grows once Turnstile mounts, so the same layout clips or
   *   doesn't depending on whether the visitor has touched the form yet.
   *   That is why this looked fine in one measurement and cut off in the
   *   next.
   * - **The cookie banner covers it.** It is `position: fixed` at the
   *   bottom with z-index 999, so on a phone it simply lies on top of a
   *   list that opens downwards.
   * - The viewport edge, on short windows.
   *
   * Whichever side has more usable room wins, and the height is capped to
   * that room — so the list scrolls internally rather than being cut off,
   * even when neither side is roomy. Measured at open time, because every
   * one of these bounds moves while the page is used.
   */
  function choosePlacement() {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();

    let clipTop = 0;
    let clipBottom = window.innerHeight;
    const banner = document.getElementById("tl-cookie-banner");
    if (banner) clipBottom = Math.min(clipBottom, banner.getBoundingClientRect().top);
    for (let el = button.parentElement; el && el !== document.body; el = el.parentElement) {
      if (getComputedStyle(el).overflowY === "visible") continue;
      const box = el.getBoundingClientRect();
      clipTop = Math.max(clipTop, box.top);
      clipBottom = Math.min(clipBottom, box.bottom);
    }

    const gap = 6;
    const margin = 8;
    const below = clipBottom - rect.bottom - gap - margin;
    const above = rect.top - clipTop - gap - margin;
    const up = below < POPOVER_MAX_HEIGHT && above > below;
    setPlacement({
      up,
      maxHeight: Math.max(96, Math.min(POPOVER_MAX_HEIGHT, up ? above : below)),
    });
  }

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const summary =
    value.length === 0
      ? t("newsletter.form.interestsPlaceholder")
      : value.map((key) => t(`newsletter.form.interests.${key}`)).join(", ");

  function toggle(key) {
    const next = value.includes(key) ? value.filter((v) => v !== key) : [...value, key];
    // Re-sorted into the canonical order so the summary line reads the same
    // regardless of the order the boxes were ticked in.
    onChange(INTERESTS.filter((i) => next.includes(i)));
  }

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          if (!open) choosePlacement();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        aria-labelledby={`${labelId} ${labelId}-value`}
        aria-invalid={invalid ? "true" : undefined}
        style={{
          ...fieldStyle,
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
        }}
      >
        {/* 0.45 alpha reached only 3.2:1 on cream and failed WCAG 1.4.3 on
            every page that carries this form (axe flagged it 40 times);
            0.6 is 5.2:1 on cream and 5.1:1 on the home page's tinted box,
            and still reads as placeholder rather than as a value. */}
        <span id={`${labelId}-value`} style={{ color: value.length ? "var(--color-ink)" : "rgba(10,9,7,0.6)" }}>
          {summary}
        </span>
        <span aria-hidden="true" style={{ fontSize: "11px", color: "rgba(10,9,7,0.5)" }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            ...(placement.up ? { bottom: "calc(100% + 6px)" } : { top: "calc(100% + 6px)" }),
            left: 0,
            right: 0,
            zIndex: 20,
            // Capped to the room actually measured on the chosen side, so
            // the list scrolls inside itself rather than running past the
            // card edge when neither side is roomy.
            maxHeight: `${placement.maxHeight}px`,
            overflowY: "auto",
            background: "var(--color-cream)",
            border: "1px solid rgba(10,9,7,0.2)",
            borderRadius: "8px",
            boxShadow: placement.up ? "0 -12px 28px rgba(10,9,7,0.18)" : "0 12px 28px rgba(10,9,7,0.18)",
            padding: "12px 14px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {INTERESTS.map((key) => (
            <label key={key} style={checkRowStyle}>
              <input
                type="checkbox"
                style={checkboxStyle}
                checked={value.includes(key)}
                onChange={() => toggle(key)}
              />
              <span>{t(`newsletter.form.interests.${key}`)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * `lang` is the language of the page the form sits on, and that is what
 * decides the newsletter's language tag: someone reading the German site
 * is signing up for the German newsletter, whatever their browser happens
 * to be configured for. `source` records the placement.
 *
 * `variant="steps"` splits the form across two screens — address and
 * topics first, name and the button second. In a narrow column the full
 * form is tall enough that the submit button sits below the fold, so the
 * one control that finishes the job is the one nobody sees. Two short
 * steps fit; the same fields, in half the height at a time.
 */
export default function NewsletterForm({ lang, source, variant = "full" }) {
  const steps = variant === "steps";
  const [step, setStep] = useState(1);
  const { t } = useTranslation();
  const [status, setStatus] = useState("idle");
  const [turnstileToken, setTurnstileToken] = useState("");
  // Turnstile is mounted on first interaction, not on render: this form
  // sits mid-page on the home page, and loading a Cloudflare script for
  // every home-page visit would be a step back from the old embed, which
  // fetched nothing until you scrolled to it.
  const [engaged, setEngaged] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: "", firstName: "", lastName: "", interests: [], honeypot: "" },
  });

  const handleVerify = useCallback((token) => setTurnstileToken(token), []);
  const handleExpire = useCallback(() => setTurnstileToken(""), []);
  const turnstilePending = Boolean(TURNSTILE_SITE_KEY) && !turnstileToken;

  // In steps mode the widget belongs to the second screen: nobody who
  // stops after the address should have caused a Cloudflare request.
  const showTurnstile = Boolean(TURNSTILE_SITE_KEY) && (steps ? step === 2 : engaged);
  const showFirstStep = !steps || step === 1;
  const showSecondStep = !steps || step === 2;

  /** Only the first screen's fields are validated on "next" — complaining
   * about an empty surname before it has been asked for would be absurd. */
  async function goToSecondStep() {
    if (await trigger(["email", "interests"])) setStep(2);
  }

  async function onSubmit(values) {
    setStatus("sending");
    try {
      const res = await fetch(`${API_BASE_URL}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, source, language: lang, turnstileToken }),
      });
      setStatus(res.ok ? "done" : "error");
    } catch {
      setStatus("error");
    }
  }

  // The confirmation step is the one thing a subscriber has to act on, and
  // it only becomes relevant once they have submitted. Standing above the
  // button it was small print nobody reads; here it is the whole message.
  if (status === "done") {
    return (
      <div
        role="status"
        style={{
          borderLeft: "3px solid var(--color-gold)",
          background: "rgba(206,149,50,0.08)",
          borderRadius: "0 8px 8px 0",
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <p style={{ margin: 0, fontSize: "16px", lineHeight: 1.6, color: "var(--color-ink)", fontWeight: 500 }}>
          {t("newsletter.form.done")}
        </p>
        <p style={{ margin: 0, fontSize: "14px", lineHeight: 1.6, color: "rgba(10,9,7,0.75)" }}>
          {t("newsletter.form.doubleOptInHint")}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      onFocusCapture={() => setEngaged(true)}
      style={{ display: "flex", flexDirection: "column", gap: "16px" }}
      noValidate
    >
      {/* Step indicator at 0.65, not the 0.55 this started at: that is
          4.38:1 on cream and fails WCAG 1.4.3 — the same value and the same
          ratio already fixed twice elsewhere in this repo. 0.65 is 6.3:1. */}
      {steps && (
        <p style={{ margin: 0, fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(10,9,7,0.65)" }}>
          {t("newsletter.form.step", { current: step, total: 2 })}
        </p>
      )}

      {showSecondStep && (
      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
        <label style={{ ...labelStyle, flex: "1 1 160px" }}>
          {t("newsletter.form.firstName")}
          <input
            type="text"
            autoComplete="given-name"
            aria-invalid={errors.firstName ? "true" : undefined}
            style={fieldStyle}
            {...register("firstName")}
          />
        </label>
        <label style={{ ...labelStyle, flex: "1 1 160px" }}>
          {t("newsletter.form.lastName")}
          <input
            type="text"
            autoComplete="family-name"
            aria-invalid={errors.lastName ? "true" : undefined}
            style={fieldStyle}
            {...register("lastName")}
          />
        </label>
      </div>
      )}
      {showSecondStep && (errors.firstName || errors.lastName) && <p style={errorStyle}>{t("newsletter.form.nameError")}</p>}

      {showFirstStep && (
      <label style={labelStyle}>
        {t("newsletter.form.email")}
        <input
          type="email"
          autoComplete="email"
          aria-invalid={errors.email ? "true" : undefined}
          style={fieldStyle}
          {...register("email")}
        />
      </label>
      )}
      {showFirstStep && errors.email && <p style={errorStyle}>{t("newsletter.form.emailError")}</p>}

      {showFirstStep && (
      <div style={labelStyle}>
        <span id="tl-newsletter-interests-label">{t("newsletter.form.interestsLabel")}</span>
        <Controller
          name="interests"
          control={control}
          render={({ field }) => (
            <InterestSelect
              value={field.value}
              onChange={field.onChange}
              invalid={Boolean(errors.interests)}
              labelId="tl-newsletter-interests-label"
            />
          )}
        />
      </div>
      )}
      {showFirstStep && errors.interests && <p style={errorStyle}>{t("newsletter.form.interestsError")}</p>}

      {/* Visually hidden rather than display:none — some bots skip hidden
          fields but fill anything they can reach in the layout. */}
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="sr-only" {...register("honeypot")} />

      {showTurnstile && (
        <TurnstileWidget siteKey={TURNSTILE_SITE_KEY} onVerify={handleVerify} onExpire={handleExpire} />
      )}

      {steps && step === 1 ? (
        <button
          type="button"
          className="tl-pill tl-pill-dark"
          style={{ alignSelf: "flex-start", border: "none", cursor: "pointer" }}
          onClick={goToSecondStep}
        >
          {t("newsletter.form.next")}
        </button>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <button
            type="submit"
            className="tl-pill tl-pill-dark"
            style={{ border: "none", cursor: "pointer" }}
            disabled={status === "sending" || ((steps || engaged) && turnstilePending)}
          >
            {status === "sending" ? t("newsletter.form.sending") : t("newsletter.form.submit")}
          </button>
          {steps && (
            <button
              type="button"
              onClick={() => setStep(1)}
              style={{ background: "none", border: "none", padding: 0, font: "inherit", fontSize: "13px", color: "rgba(10,9,7,0.65)", textDecoration: "underline", cursor: "pointer" }}
            >
              {t("newsletter.form.back")}
            </button>
          )}
        </div>
      )}

      {/* Consent is the act of subscribing plus confirming the opt-in mail,
          so this is a notice rather than a tick box. In steps mode it sits
          with the button that acts on it, not two screens earlier. */}
      {showSecondStep && (
      <p style={{ fontSize: "12px", lineHeight: 1.6, color: "rgba(10,9,7,0.6)", margin: 0 }}>
        {t("newsletter.form.consentInfo")}{" "}
        <a href={`/${lang}/${DATENSCHUTZ_PATH[lang]}`}>{t("newsletter.form.consentLink")}</a>
        {"."}
      </p>
      )}

      {status === "error" && (
        <p role="alert" style={{ ...errorStyle, fontSize: "14px" }}>
          {t("newsletter.form.error")}
        </p>
      )}
    </form>
  );
}
